import { useMemo, useState } from "react";
import { format } from "date-fns";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import Loading from "components/Loading";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import api from "services/api";
import { useTranslate } from "utils/i18n";
import { useRegisterAssistantPage } from "assistant/useRegisterAssistantPage";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";

const CARD_SHELL_SX = {
  overflow: "hidden",
  borderRadius: "16px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
  bgcolor: figmaTokens.surface,
};

const statWrapSx = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  "& > .MuiCard-root": {
    flex: 1,
    minHeight: 108,
    borderRadius: "16px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
    backgroundColor: figmaTokens.surface,
    backgroundImage: `linear-gradient(145deg, rgba(118, 75, 162, 0.08) 0%, ${figmaTokens.surface} 52%)`,
    overflow: "hidden",
  },
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("tk446655440000")}` };
}

function defaultStartDate() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return format(d, "yyyy-MM-dd");
}

function defaultEndDate() {
  return format(new Date(), "yyyy-MM-dd");
}

function parseListPayload(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function groupByProvince(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const key = row?.provinceId ?? row?.provinceName ?? "unknown";
    if (!map.has(key)) {
      map.set(key, {
        provinceId: row?.provinceId,
        provinceName: row?.provinceName ?? "—",
        departments: [],
        teachersAssigned: 0,
        teachersChecked: 0,
      });
    }
    const group = map.get(key);
    group.departments.push(row);
    group.teachersAssigned += Number(row?.teachersAssigned ?? 0);
    group.teachersChecked += Number(row?.teachersChecked ?? 0);
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a.provinceName).localeCompare(String(b.provinceName))
  );
}

function checkRate(checked, assigned) {
  const a = Number(assigned) || 0;
  const c = Number(checked) || 0;
  if (a <= 0) return 0;
  return Math.round((c / a) * 100);
}

function rateChipSx(rate) {
  if (rate >= 75) {
    return { bgcolor: "rgba(16, 185, 129, 0.14)", color: "#047857", border: "1px solid rgba(16, 185, 129, 0.35)" };
  }
  if (rate >= 40) {
    return { bgcolor: "rgba(245, 158, 11, 0.14)", color: "#b45309", border: "1px solid rgba(245, 158, 11, 0.35)" };
  }
  return { bgcolor: "rgba(239, 68, 68, 0.1)", color: "#b91c1c", border: "1px solid rgba(239, 68, 68, 0.28)" };
}

function AreaAttendanceSummary() {
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [daysPresent, setDaysPresent] = useState("1");
  const [lastQuery, setLastQuery] = useState(null);

  const provinceGroups = useMemo(() => groupByProvince(rows), [rows]);

  const totals = useMemo(() => {
    const assigned = rows.reduce((s, r) => s + Number(r?.teachersAssigned ?? 0), 0);
    const checked = rows.reduce((s, r) => s + Number(r?.teachersChecked ?? 0), 0);
    return {
      provinces: provinceGroups.length,
      departments: rows.length,
      assigned,
      checked,
      rate: checkRate(checked, assigned),
    };
  }, [rows, provinceGroups.length]);

  const assistantPageSnapshot = useMemo(
    () => ({
      pageKey: "area-attendance",
      route: "/synthese-presence-zone",
      filters: {
        startDate,
        endDate,
        days: Number(daysPresent) || 1,
        assigned: totals.assigned,
        checked: totals.checked,
        lastQuery,
      },
    }),
    [startDate, endDate, daysPresent, totals.assigned, totals.checked, lastQuery]
  );
  useRegisterAssistantPage(assistantPageSnapshot);

  const loadSummary = async (event) => {
    event?.preventDefault?.();
    setError("");

    if (!startDate || !endDate) {
      setError(t("area_attendance.error.dates", "Please select start and end dates."));
      return;
    }
    if (startDate > endDate) {
      setError(t("area_attendance.error.date_order", "Start date must be before end date."));
      return;
    }
    const daysNum = Number(daysPresent);
    if (!Number.isFinite(daysNum) || daysNum < 1) {
      setError(t("area_attendance.error.days", "Please enter a valid minimum days present (1 or more)."));
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get("/Teacher/areaattendancesummary", {
        params: {
          startDate: String(startDate).slice(0, 10),
          endDate: String(endDate).slice(0, 10),
          days: daysNum,
        },
        headers: authHeaders(),
      });
      const list = parseListPayload(data);
      setRows(list);
      setLastQuery({ startDate, endDate, days: daysNum });
      if (!list.length) {
        setError(t("area_attendance.no_results", "No data found for the selected criteria."));
      }
    } catch (err) {
      console.error("Area attendance summary error", err);
      setRows([]);
      setLastQuery(null);
      setError(t("area_attendance.error.load", "Failed to load the area attendance summary."));
    } finally {
      setLoading(false);
    }
  };

  const queryHint =
    lastQuery &&
    t("area_attendance.query_hint", "{{start}} → {{end}} · min. {{days}} day(s) present")
      .replace("{{start}}", lastQuery.startDate)
      .replace("{{end}}", lastQuery.endDate)
      .replace("{{days}}", String(lastQuery.days));

  return (
    <DashboardLayout>
      {loading && <Loading />}
      <DashboardNavbar />
      <SoftBox py={3} className="content-area-main" sx={{ bgcolor: figmaTokens.pageBg }}>
        <Card sx={{ ...CARD_SHELL_SX, mb: 3 }}>
          <SoftBox sx={{ height: 5, background: figmaTokens.gradientCSS }} />
          <SoftBox sx={{ p: { xs: 2, md: 3 } }}>
            <SoftTypography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: "0.14em", color: figmaTokens.textMuted, fontSize: "0.68rem" }}
            >
              {t("area_attendance.page_kicker", "REGIONAL ATTENDANCE")}
            </SoftTypography>
            <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
              {t("area_attendance.title", "Area attendance summary")}
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 680 }}>
              {t(
                "area_attendance.page_hint",
                "Compare assigned teachers with those who checked in for at least the minimum number of days, broken down by province and department."
              )}
            </SoftTypography>
          </SoftBox>
        </Card>

        <Card sx={CARD_SHELL_SX}>
          <SoftBox
            component="form"
            onSubmit={loadSummary}
            sx={{ p: { xs: 2, md: 3 }, borderBottom: rows.length ? `1px solid ${figmaTokens.cardHeaderRule}` : "none" }}
          >
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("area_attendance.start_date", "Start date")}
                </SoftTypography>
                <SoftInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("area_attendance.end_date", "End date")}
                </SoftTypography>
                <SoftInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("area_attendance.days_present", "Min. days present")}
                </SoftTypography>
                <SoftInput
                  type="number"
                  min={1}
                  value={daysPresent}
                  onChange={(e) => setDaysPresent(e.target.value)}
                />
                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                  {t(
                    "area_attendance.days_hint",
                    "Teachers must have checked in on at least this many days to count as checked."
                  )}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftButton type="submit" variant="gradient" color="info" fullWidth sx={{ fontWeight: 700 }}>
                  <Icon sx={{ mr: 0.75, fontSize: "1.1rem !important" }}>analytics</Icon>
                  {t("area_attendance.search", "Load summary")}
                </SoftButton>
              </Grid>
            </Grid>
            {error && (
              <Alert severity={rows.length ? "info" : "warning"} sx={{ mt: 2, borderRadius: "12px" }}>
                {error}
              </Alert>
            )}
          </SoftBox>

          {rows.length > 0 && (
            <SoftBox sx={{ p: { xs: 2, md: 3 } }}>
              {queryHint && (
                <SoftTypography variant="caption" sx={{ display: "block", mb: 2, fontWeight: 700, color: figmaTokens.textMuted }}>
                  {queryHint}
                </SoftTypography>
              )}

              <SoftBox
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                  mb: 3,
                }}
              >
                <SoftBox sx={statWrapSx}>
                  <MiniStatisticsCard
                    title={{ text: t("area_attendance.stats.provinces", "Provinces") }}
                    count={totals.provinces}
                    percentage={{ color: "success", text: "" }}
                    icon={{ color: "info", component: "map" }}
                  />
                </SoftBox>
                <SoftBox sx={statWrapSx}>
                  <MiniStatisticsCard
                    title={{ text: t("area_attendance.stats.departments", "Departments") }}
                    count={totals.departments}
                    percentage={{ color: "success", text: "" }}
                    icon={{ color: "primary", component: "account_tree" }}
                  />
                </SoftBox>
                <SoftBox sx={statWrapSx}>
                  <MiniStatisticsCard
                    title={{ text: t("area_attendance.stats.assigned", "Teachers assigned") }}
                    count={totals.assigned}
                    percentage={{ color: "success", text: "" }}
                    icon={{ color: "secondary", component: "assignment_ind" }}
                  />
                </SoftBox>
                <SoftBox sx={statWrapSx}>
                  <MiniStatisticsCard
                    title={{ text: t("area_attendance.stats.checked", "Teachers checked") }}
                    count={totals.checked}
                    percentage={{ color: "success", text: `${totals.rate}%` }}
                    icon={{ color: "success", component: "fact_check" }}
                  />
                </SoftBox>
              </SoftBox>

              {provinceGroups.map((province) => (
                <SoftBox key={province.provinceId ?? province.provinceName} mb={3}>
                  <SoftBox
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1.5}
                    mb={1.25}
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderRadius: "12px",
                      bgcolor: "rgba(102, 126, 234, 0.07)",
                      border: `1px solid rgba(102, 126, 234, 0.18)`,
                    }}
                  >
                    <SoftBox>
                      <SoftTypography variant="button" sx={{ fontWeight: 800, color: figmaTokens.textPrimary }}>
                        {province.provinceName}
                      </SoftTypography>
                      <SoftTypography variant="caption" sx={{ display: "block", color: figmaTokens.textSecondary, mt: 0.25 }}>
                        {t("area_attendance.province_departments", "{{n}} department(s)").replace(
                          "{{n}}",
                          String(province.departments.length)
                        )}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" flexWrap="wrap" gap={1}>
                      <Chip
                        size="small"
                        label={t("area_attendance.chip.assigned", "{{n}} assigned").replace(
                          "{{n}}",
                          String(province.teachersAssigned)
                        )}
                        sx={{ fontWeight: 700, bgcolor: figmaTokens.surface }}
                      />
                      <Chip
                        size="small"
                        label={t("area_attendance.chip.checked", "{{n}} checked").replace(
                          "{{n}}",
                          String(province.teachersChecked)
                        )}
                        sx={{ fontWeight: 700, bgcolor: figmaTokens.surface }}
                      />
                      <Chip
                        size="small"
                        label={`${checkRate(province.teachersChecked, province.teachersAssigned)}%`}
                        sx={{ fontWeight: 800, ...rateChipSx(checkRate(province.teachersChecked, province.teachersAssigned)) }}
                      />
                    </SoftBox>
                  </SoftBox>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: "14px",
                      border: `1px solid ${figmaTokens.borderSubtle}`,
                      overflow: "auto",
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        minWidth: 640,
                        ...directoryTableHeadStickyOverridesSx,
                        ...directoryTableBodyCellDefaultsSx,
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={directoryTableHeaderCellSx}>
                            {t("area_attendance.col.department", "Department")}
                          </TableCell>
                          <TableCell align="right" sx={directoryTableHeaderCellSx}>
                            {t("area_attendance.col.assigned", "Assigned")}
                          </TableCell>
                          <TableCell align="right" sx={directoryTableHeaderCellSx}>
                            {t("area_attendance.col.checked", "Checked")}
                          </TableCell>
                          <TableCell align="right" sx={directoryTableHeaderCellSx}>
                            {t("area_attendance.col.rate", "Check rate")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {province.departments
                          .slice()
                          .sort((a, b) =>
                            String(a.departmentName ?? "").localeCompare(String(b.departmentName ?? ""))
                          )
                          .map((row) => {
                            const assigned = Number(row.teachersAssigned ?? 0);
                            const checked = Number(row.teachersChecked ?? 0);
                            const rate = checkRate(checked, assigned);
                            return (
                              <TableRow key={row.departmentId ?? row.departmentName} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{row.departmentName ?? "—"}</TableCell>
                                <TableCell align="right">{assigned}</TableCell>
                                <TableCell align="right">{checked}</TableCell>
                                <TableCell align="right">
                                  <Chip size="small" label={`${rate}%`} sx={{ fontWeight: 800, ...rateChipSx(rate) }} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        <TableRow sx={{ bgcolor: "rgba(247, 249, 252, 0.9)" }}>
                          <TableCell sx={{ fontWeight: 800 }}>
                            {t("area_attendance.row.province_total", "Province total")}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>
                            {province.teachersAssigned}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>
                            {province.teachersChecked}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`${checkRate(province.teachersChecked, province.teachersAssigned)}%`}
                              sx={{
                                fontWeight: 800,
                                ...rateChipSx(checkRate(province.teachersChecked, province.teachersAssigned)),
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SoftBox>
              ))}

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  mt: 1,
                  borderRadius: "14px",
                  border: `1px solid ${figmaTokens.borderStrong}`,
                  overflow: "auto",
                }}
              >
                <SoftTypography
                  variant="button"
                  sx={{ display: "block", px: 2, pt: 2, pb: 1, fontWeight: 800, color: figmaTokens.textPrimary }}
                >
                  {t("area_attendance.all_rows_title", "Full list — all provinces & departments")}
                </SoftTypography>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    minWidth: 720,
                    ...directoryTableHeadStickyOverridesSx,
                    ...directoryTableBodyCellDefaultsSx,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={directoryTableHeaderCellSx}>
                        {t("area_attendance.col.province", "Province")}
                      </TableCell>
                      <TableCell sx={directoryTableHeaderCellSx}>
                        {t("area_attendance.col.department", "Department")}
                      </TableCell>
                      <TableCell align="right" sx={directoryTableHeaderCellSx}>
                        {t("area_attendance.col.assigned", "Assigned")}
                      </TableCell>
                      <TableCell align="right" sx={directoryTableHeaderCellSx}>
                        {t("area_attendance.col.checked", "Checked")}
                      </TableCell>
                      <TableCell align="right" sx={directoryTableHeaderCellSx}>
                        {t("area_attendance.col.rate", "Check rate")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows
                      .slice()
                      .sort((a, b) => {
                        const p = String(a.provinceName ?? "").localeCompare(String(b.provinceName ?? ""));
                        if (p !== 0) return p;
                        return String(a.departmentName ?? "").localeCompare(String(b.departmentName ?? ""));
                      })
                      .map((row) => {
                        const assigned = Number(row.teachersAssigned ?? 0);
                        const checked = Number(row.teachersChecked ?? 0);
                        const rate = checkRate(checked, assigned);
                        return (
                          <TableRow key={`${row.provinceId}-${row.departmentId}`} hover>
                            <TableCell>{row.provinceName ?? "—"}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{row.departmentName ?? "—"}</TableCell>
                            <TableCell align="right">{assigned}</TableCell>
                            <TableCell align="right">{checked}</TableCell>
                            <TableCell align="right">
                              <Chip size="small" label={`${rate}%`} sx={{ fontWeight: 800, ...rateChipSx(rate) }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    <TableRow sx={{ bgcolor: "rgba(102, 126, 234, 0.08)" }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 800 }}>
                        {t("area_attendance.row.grand_total", "Grand total")}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>
                        {totals.assigned}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>
                        {totals.checked}
                      </TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={`${totals.rate}%`} sx={{ fontWeight: 800, ...rateChipSx(totals.rate) }} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </SoftBox>
          )}
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AreaAttendanceSummary;
